import { DetailedHTMLProps, HTMLAttributes, ReactElement, cloneElement, isValidElement, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type HTMLElementProps = DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>;

/**
 * A wrapper component that adds text truncation with smart tooltip behavior to any HTML element.
 *
 * This component automatically:
 * - Applies the Tailwind `truncate` class to truncate overflowing text with ellipsis
 * - Shows tooltip with the full text content, but only when the text is actually truncated
 * - Preserves all existing props and classes on the child element
 *
 * @example
 * ```tsx
 * // Simple usage with a span
 * <TruncateWithTooltip>
 *   <span className="text-gray-900">This is some very long text that will be truncated</span>
 * </TruncateWithTooltip>
 *
 * // With an h4 heading
 * <TruncateWithTooltip>
 *   <h4 className="text-lg font-bold">Long heading text that might overflow</h4>
 * </TruncateWithTooltip>
 *
 * // With nested elements (tooltip will show combined text)
 * <TruncateWithTooltip>
 *   <p className="text-sm">
 *     Some text with <strong>bold</strong> and <em>italic</em> parts
 *   </p>
 * </TruncateWithTooltip>
 * ```
 *
 * @param children - A single React element to apply truncation behavior to.
 *                   Must be an HTML element (span, div, p, h1-h6, etc.)
 *
 * @returns The child element with truncation and smart tooltip behavior applied
 *
 * @remarks
 * - Only shows tooltip when text is actually truncated (no unnecessary tooltips)
 * - Merges the `truncate` class with existing classes using `tailwind-merge` to handle conflicts
 * - Requires exactly one child element - will log error and return child unchanged if invalid
 */
export const TruncateWithTooltip = ({ children }: { children: ReactElement<HTMLElementProps> }) => {
  const elementRef = useRef<HTMLElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  if (!isValidElement(children)) {
    console.error("TruncateWithTooltip requires exactly one React element as a child");
    return <>{children}</>;
  }

  const handleMouseEnter = () => {
    if (elementRef.current) {
      const isTruncated = elementRef.current.scrollWidth > elementRef.current.offsetWidth;
      setShowTooltip(isTruncated);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return cloneElement<HTMLElementProps>(children, {
    ref: elementRef,
    className: twMerge("truncate", children.props.className),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    title: showTooltip ? elementRef.current?.textContent || undefined : undefined,
  });
};
